import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Wand2, BookMarked } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export default function TraegerTemplates({ formId, onApplied }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const ref = useRef(null);

  const load = useCallback(() => api.get("/traeger-forms/templates").then((r) => setList(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const learning = list.some((t) => t.status === "learning");
  useEffect(() => {
    if (!learning) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [learning, load]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/traeger-forms/templates", fd, { timeout: 120000 });
      toast.success("Beispiel wird ausgewertet – das dauert ca. 1–2 Minuten");
      load();
    } catch (e) { err(e); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const remove = async (t) => {
    if (!window.confirm(`Vorlage „${t.name}" löschen?`)) return;
    try { await api.delete(`/traeger-forms/templates/${t.template_id}`); toast.success("Vorlage gelöscht"); load(); } catch (e) { err(e); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/traeger-forms/form/${formId}/apply-template`, {}, { timeout: 120000 });
      toast.success(`Vorlage „${r.data.template.name}" übernommen (${r.data.fields.length} Felder)`);
      onApplied?.();
    } catch (e) { err(e); } finally { setApplying(false); }
  };

  return (
    <details className="border border-slate-200 bg-white" data-testid="traeger-templates">
      <summary className="cursor-pointer px-4 py-2.5 text-sm text-slate-700 flex items-center gap-2">
        <BookMarked size={14} className="text-[#0a788c]" /> Positions-Vorlagen ({list.length})
      </summary>
      <div className="px-4 pb-4 space-y-3">
        <p className="text-xs text-slate-500">
          Sitzt bei einem Vordruck etwas an der falschen Stelle? Lade hier ein <b>korrekt ausgefülltes Beispiel</b> dieses Vordrucks hoch
          (als PDF, von dir per Hand ausgefüllt). Das Portal merkt sich, welcher Wert wohin gehört – und füllt jeden neuen Kunden mit
          demselben Vordruck ab sofort genau so aus.
        </p>
        <div className="flex flex-wrap gap-2">
          <input ref={ref} type="file" accept="application/pdf" className="hidden" data-testid="traeger-template-file-input"
            onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          <button onClick={() => ref.current?.click()} disabled={busy} data-testid="traeger-template-upload-btn"
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Ausgefülltes Beispiel hochladen
          </button>
          {formId && (
            <button onClick={apply} disabled={applying} data-testid="traeger-template-apply-btn"
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-sm hover:bg-slate-50 transition-colors disabled:opacity-50">
              {applying ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Vorlage auf diesen Vordruck anwenden
            </button>
          )}
        </div>
        {busy && <div className="text-xs text-slate-500">Das Beispiel wird ausgewertet – das dauert bis zu 2 Minuten.</div>}
        {list.length > 0 && (
          <ul className="divide-y border border-slate-200" data-testid="traeger-template-list">
            {list.map((t) => (
              <li key={t.template_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`traeger-template-${t.template_id}`}>
                <div className="min-w-0">
                  <div className="truncate text-slate-800 flex items-center gap-2">
                    {t.name}
                    {t.status === "learning" && <span className="inline-flex items-center gap-1 text-[10px] text-amber-700"><Loader2 size={10} className="animate-spin" /> wird gelernt …</span>}
                    {t.status === "failed" && <span className="text-[10px] text-rose-700">fehlgeschlagen</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t.status === "failed" ? (t.error || "Kein Feld erkannt")
                      : t.status === "learning" ? "Das Beispiel wird ausgewertet – ca. 1–2 Minuten"
                      : `${t.field_count} Felder · Seiten ${t.pages.map((p) => p.page).join(", ") || "—"} · gelernt von ${t.created_by_name || "—"}`}
                  </div>
                </div>
                <button onClick={() => remove(t)} className="text-rose-700 hover:text-rose-900 transition-colors shrink-0" data-testid={`traeger-template-delete-${t.template_id}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}
